/**
 * Преобразует код языка i18next в код локали для форматирования даты/времени
 * @param localeCode - код языка из i18next (например, 'ru', 'en', 'tg')
 * @returns код локали для форматирования даты/времени (например, 'ru-RU', 'en-US')
 */
const convertLocaleCode = (localeCode: string): string => {
  switch (localeCode) {
    case 'ru':
      return 'ru-RU';
    case 'en':
      return 'en-US';
    case 'tg':  // Таджикский
      return 'tg-TJ';
    case 'ky':  // Киргизский
      return 'ky-KG';
    case 'uz':  // Узбекский
      return 'uz-UZ';
    case 'kk':  // Казахский
      return 'kk-KZ';
    default:
      return localeCode.includes('-') ? localeCode : `${localeCode}-${localeCode.toUpperCase()}`;
  }
};

/**
 * Форматирует дату в соответствии с локалью пользователя
 * @param date - дата для форматирования
 * @param locale - локаль для форматирования
 * @returns отформатированная строка даты в формате dd.mm.yyyy
 */
export const formatDateToLocal = (date: string | Date, locale: string = 'ru-RU'): string => {
  const convertedLocale = convertLocaleCode(locale);
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Используем формат dd.mm.yyyy для всех локалей
  return dateObj.toLocaleDateString(convertedLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Форматирует время в соответствии с локалью пользователя
 * @param date - дата для форматирования времени
 * @param locale - локаль для форматирования
 * @returns отформатированная строка времени в формате HH:mm (24-часовой формат)
 */
export const formatTimeToLocal = (date: string | Date, locale: string = 'ru-RU'): string => {
  const convertedLocale = convertLocaleCode(locale);
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Для всех локалей используем 24-часовой формат времени
  return dateObj.toLocaleTimeString(convertedLocale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false  // 24-часовой формат для всех локалей
  });
};

/**
 * Форматирует дату и время в соответствии с локалью пользователя
 * @param date - дата для форматирования
 * @param locale - локаль для форматирования
 * @returns отформатированная строка даты и времени в формате dd.mm.yyyy HH:mm
 */
export const formatDateTimeToLocal = (date: string | Date, locale: string = 'ru-RU'): string => {
  const convertedLocale = convertLocaleCode(locale);
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Форматируем дату в dd.mm.yyyy
  const datePart = dateObj.toLocaleDateString(convertedLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  
  // Форматируем время в HH:mm (24-часовой формат)
  const timePart = dateObj.toLocaleTimeString(convertedLocale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false  // 24-часовой формат для всех локалей
  });
  
  return `${datePart} ${timePart}`;
};

/**
 * Форматирует число в соответствии с локалью пользователя
 * @param num - число для форматирования
 * @param locale - локаль для форматирования
 * @param options - дополнительные опции форматирования чисел
 * @returns отформатированная строка числа
 */
export const formatNumber = (num: number, locale: string = 'ru-RU', options?: Intl.NumberFormatOptions): string => {
  const convertedLocale = convertLocaleCode(locale);
  return num.toLocaleString(convertedLocale, options);
};

/**
 * Форматирует дату в соответствии с локалью и опциями
 * @param date - дата для форматирования
 * @param locale - локаль для форматирования
 * @param options - опции форматирования даты
 * @returns отформатированная строка даты
 */
export const formatDate = (
  date: string | Date,
  locale: string = 'ru',
  options?: Intl.DateTimeFormatOptions
): string => {
  const convertedLocale = convertLocaleCode(locale);
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };
  
  return dateObj.toLocaleDateString(convertedLocale, options || defaultOptions);
};